/**
 * AcademicQuizPrompt.jsx — post-reading retention quiz for Academic Library.
 *
 * Feb 12 2026 user spec:
 *   *"When a player is done with document prompt test for reward. award
 *    2 coins for each correct answer. 3 questions for every 2k words,
 *    let the reader choose how many questions they want to answer and
 *    tell them the reward at prompt."*
 *
 * Three stages: PROMPT (chooser) → QUIZ (the questions) → DONE (result).
 * Uses the same AI quiz endpoints we just added to
 * /app/backend/routes/academic_library_routes.py:
 *   GET  /api/academic-library/{id}/retention-quiz/prompt
 *   POST /api/academic-library/{id}/retention-quiz/generate?count=N
 *   POST /api/academic-library/{id}/retention-quiz/submit
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X as XIcon, Loader2, Trophy, Sparkles, Check, AlertCircle } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Stage = {
  PROMPT: 'prompt',
  QUIZ:   'quiz',
  DONE:   'done',
};

const AcademicQuizPrompt = ({ itemId, open, onClose }) => {
  const [stage, setStage] = useState(Stage.PROMPT);
  const [prompt, setPrompt] = useState(null);
  const [count, setCount] = useState(3);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !itemId) return;
    let cancelled = false;
    setStage(Stage.PROMPT);
    setPrompt(null);
    setQuiz(null);
    setAnswers({});
    setResult(null);
    setErr('');
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/academic-library/${itemId}/retention-quiz/prompt`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) { setErr(d.detail || `Could not load (${r.status})`); return; }
        setPrompt(d);
        setCount(Math.min(3, d.max_questions || 3));
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Network error');
      }
    })();
    return () => { cancelled = true; };
  }, [open, itemId]);

  const potentialCoins = useMemo(() => count * 2, [count]);

  const generate = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/academic-library/${itemId}/retention-quiz/generate?count=${count}`,
        { method: 'POST' },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `Could not generate (${r.status})`); return; }
      setQuiz(d);
      setStage(Stage.QUIZ);
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!quiz) return;
    setBusy(true);
    setErr('');
    try {
      const orderedAnswers = quiz.questions.map((_, i) => (answers[i] ?? -1));
      const r = await authFetch(
        `${BACKEND_URL}/api/academic-library/${itemId}/retention-quiz/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quiz_id: quiz.quiz_id, answers: orderedAnswers }),
        },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `Could not submit (${r.status})`); return; }
      setResult(d);
      setStage(Stage.DONE);
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm"
      data-testid="academic-quiz-overlay"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl bg-[#0f1117] border border-white/10 max-h-[90vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}
        data-testid="academic-quiz-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
            {stage === Stage.PROMPT && 'Earn coins'}
            {stage === Stage.QUIZ   && 'Retention quiz'}
            {stage === Stage.DONE   && 'Result'}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            data-testid="academic-quiz-close"
            aria-label="Close quiz"
          >
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-5">
          {err && (
            <div className="mb-3 px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-400/30 text-rose-200 text-xs flex items-start gap-2" data-testid="academic-quiz-err">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {/* PROMPT */}
          {stage === Stage.PROMPT && (
            <>
              {!prompt && !err && (
                <div className="flex items-center justify-center py-6 text-slate-400 text-sm gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              )}
              {prompt && (
                <>
                  <h2 className="text-lg font-bold text-white">
                    Test your retention 🎓
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">
                    {prompt.title}
                  </p>
                  <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Reward</p>
                    <p className="text-sm text-white mt-1">
                      <span className="font-bold text-amber-200">{prompt.coins_per_correct} coins</span> per correct answer
                      {prompt.xp_on_submit > 0 && (
                        <> · <span className="font-bold text-amber-200">+{prompt.xp_on_submit.toLocaleString()} Reader XP</span> on submit</>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      Up to {prompt.max_questions} questions ({prompt.word_count.toLocaleString()} words, 3 questions per 2k words).
                    </p>
                  </div>

                  <div className="mt-4">
                    <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                      How many questions?
                    </label>
                    <input
                      type="range"
                      min={prompt.min_questions}
                      max={prompt.max_questions}
                      step="1"
                      value={count}
                      onChange={(ev) => setCount(parseInt(ev.target.value, 10) || prompt.min_questions)}
                      className="w-full mt-2 accent-amber-400"
                      data-testid="academic-quiz-count-slider"
                    />
                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
                      <span>{prompt.min_questions}</span>
                      <span className="text-amber-200 font-bold tabular-nums">{count} Qs · max {potentialCoins} coins</span>
                      <span>{prompt.max_questions}</span>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm"
                      data-testid="academic-quiz-skip"
                    >
                      Skip for now
                    </button>
                    <button
                      type="button"
                      onClick={generate}
                      disabled={busy}
                      className="px-3 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="academic-quiz-start"
                    >
                      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                      {busy ? 'Generating…' : `Start · earn up to ${potentialCoins} coins`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* QUIZ */}
          {stage === Stage.QUIZ && quiz && (
            <>
              <p className="text-xs text-slate-400">
                {quiz.count} questions · {quiz.coins_per_correct} coins per correct
              </p>
              <div className="mt-3 space-y-4">
                {quiz.questions.map((q, qi) => (
                  <div key={qi} className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`academic-quiz-q-${qi}`}>
                    <p className="text-sm font-bold text-white mb-2">
                      <span className="text-amber-300">Q{qi + 1}.</span> {q.question}
                    </p>
                    <div className="space-y-1.5">
                      {q.options.map((opt, oi) => {
                        const picked = answers[qi] === oi;
                        return (
                          <button
                            key={oi}
                            type="button"
                            onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                            className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${picked ? 'bg-amber-500/20 border-amber-400/50 text-white' : 'bg-white/[0.02] border-white/10 text-slate-200 hover:bg-white/5'}`}
                            data-testid={`academic-quiz-q-${qi}-opt-${oi}`}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-widest mr-2 opacity-70">
                              {String.fromCharCode(65 + oi)}
                            </span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={busy || Object.keys(answers).length < quiz.count}
                className="mt-5 w-full px-3 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="academic-quiz-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? 'Grading…' : 'Submit answers'}
              </button>
            </>
          )}

          {/* DONE */}
          {stage === Stage.DONE && result && (
            <div className="text-center py-2" data-testid="academic-quiz-done">
              <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/20 border border-amber-400/40 grid place-items-center mb-3">
                <Trophy className="w-7 h-7 text-amber-300" />
              </div>
              <h2 className="text-2xl font-black text-white">
                {result.correct_count} / {result.total_questions}
              </h2>
              <p className="text-sm text-slate-400 mt-1">correct answers</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Coins earned</p>
                  <p className="text-2xl font-black text-white tabular-nums mt-1">+{result.coins_awarded}</p>
                </div>
                <div className="rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">Reader XP</p>
                  <p className="text-2xl font-black text-white tabular-nums mt-1">+{result.xp_awarded.toLocaleString()}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 w-full px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm inline-flex items-center justify-center gap-2"
                data-testid="academic-quiz-done-close"
              >
                <Check className="w-4 h-4" /> Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcademicQuizPrompt;
